/* Akel Beauty — accessible product search drawer with Shopify suggestions. */

(function () {
  'use strict';

  function initSearchDrawer() {
    var drawer = document.querySelector('[data-search-drawer]');
    var opener = document.querySelector('[data-search-open]');
    if (!drawer || !opener) return;

    var form = drawer.querySelector('[data-search-form]');
    var input = drawer.querySelector('[data-search-input]');
    var results = drawer.querySelector('[data-search-results]');
    var status = drawer.querySelector('[data-search-status]');
    var hint = drawer.querySelector('[data-search-hint]');
    var endpoint = drawer.getAttribute('data-predictive-search-url');
    if (!form || !input || !results || !endpoint) return;

    var cache = Object.create(null);
    var timer = null;
    var controller = null;
    var activeIndex = -1;
    var lastFocused = null;

    function options() {
      return Array.prototype.slice.call(results.querySelectorAll('[data-search-option]'));
    }

    function setAnnouncement(message) {
      if (status) status.textContent = message || '';
    }

    function setBusy(isBusy) {
      results.setAttribute('aria-busy', String(isBusy));
      if (isBusy) setAnnouncement(drawer.getAttribute('data-search-loading'));
    }

    function resetSelection() {
      options().forEach(function (option) {
        option.setAttribute('aria-selected', 'false');
        option.removeAttribute('id');
      });
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }

    function clearResults() {
      resetSelection();
      results.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
      results.setAttribute('aria-busy', 'false');
      if (hint) hint.hidden = false;
      setAnnouncement('');
    }

    function renderResponse(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var content = parsed.querySelector('[data-predictive-content]');
      results.replaceChildren();

      if (!content) {
        input.setAttribute('aria-expanded', 'false');
        setAnnouncement(drawer.getAttribute('data-search-error'));
        return;
      }

      results.appendChild(document.importNode(content, true));
      if (hint) hint.hidden = true;
      input.setAttribute('aria-expanded', 'true');
      results.setAttribute('aria-busy', 'false');
      resetSelection();
      setAnnouncement(content.getAttribute('data-predictive-announcement'));
    }

    function requestSuggestions(query) {
      var normalized = query.trim().toLocaleLowerCase();
      if (normalized.length < 2) {
        if (controller) controller.abort();
        clearResults();
        return;
      }

      if (cache[normalized]) {
        renderResponse(cache[normalized]);
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();
      resetSelection();
      results.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
      if (hint) hint.hidden = true;
      setBusy(true);

      var url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query.trim());
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      url.searchParams.set('section_id', 'predictive-search');

      fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: controller.signal
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed');
          return response.text();
        })
        .then(function (html) {
          if (input.value.trim().toLocaleLowerCase() !== normalized) return;
          cache[normalized] = html;
          renderResponse(html);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          results.setAttribute('aria-busy', 'false');
          input.setAttribute('aria-expanded', 'false');
          var errorMessage = drawer.getAttribute('data-search-error');
          var errorNode = document.createElement('p');
          errorNode.className = 'predictive-search__empty';
          errorNode.textContent = errorMessage;
          results.replaceChildren(errorNode);
          setAnnouncement(errorMessage);
        });
    }

    function scheduleSearch() {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        requestSuggestions(input.value);
      }, 220);
    }

    function select(index) {
      var items = options();
      if (!items.length) return;
      activeIndex = (index + items.length) % items.length;

      items.forEach(function (item, itemIndex) {
        var isActive = itemIndex === activeIndex;
        item.setAttribute('aria-selected', String(isActive));
        item.id = 'PredictiveSearchOption-' + itemIndex;
      });

      input.setAttribute('aria-activedescendant', items[activeIndex].id);
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    function focusableElements() {
      return Array.prototype.slice.call(
        drawer.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"])'
        )
      ).filter(function (element) {
        return !element.hidden && element.offsetParent !== null;
      });
    }

    function open(event) {
      if (event) event.preventDefault();
      lastFocused = document.activeElement;
      drawer.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(function () {
        input.focus();
        if (input.value.trim().length >= 2) scheduleSearch();
      });
    }

    function close() {
      window.clearTimeout(timer);
      if (controller) controller.abort();
      drawer.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      clearResults();
      if (lastFocused) lastFocused.focus();
    }

    opener.addEventListener('click', open);
    drawer.querySelectorAll('[data-search-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });
    input.addEventListener('input', scheduleSearch);

    input.addEventListener('keydown', function (event) {
      var items = options();
      if (event.key === 'ArrowDown' && items.length) {
        event.preventDefault();
        select(activeIndex + 1);
      } else if (event.key === 'ArrowUp' && items.length) {
        event.preventDefault();
        select(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
        event.preventDefault();
        items[activeIndex].click();
      }
    });

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      var focusable = focusableElements();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchDrawer);
  } else {
    initSearchDrawer();
  }
})();
